//
// Use selected OR selection
//
// name: the radio button group name
// id: this very button's identifier
//

import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useCallback,
} from 'react';

import { logError } from '../../utils';
import { Row } from '../display';

function RadioButton(props) {
  const {
    // static, changes are ignored:
    id,
    name,
    // dynamic (changes are reflected):
    className,
    onChange,
    selected,
    selection,
    disabled,
    label,
    color,
  } = props;

  if ((selected !== undefined) && (selection !== undefined)) {
    logError(
      `RadioButton name=${name}, do NOT use selected and selection at the same time`,
      'RadioButton.selectedAndSelectionConflict',
      { name, id },
    );
    throw new Error('RadioButton wrong input');
  }

  const isSelected = selected || (selection && selection.includes(name));

  const handleClick = useCallback(
    () => {
      // console.log(`RadioButton click ${name}`);
      onChange({
        currentTarget: {
          id,
          name,
          value: !isSelected,
        },
      });
    },
    [onChange, id, name, isSelected],
  );

  const buttonContent = (
    <div
      className={classNames(
        'w-full h-full border-2 rounded-full p-0.5 relative flex items-center justify-center',
        {
          'border-pxCheckActive': (!disabled) && onChange,
          'border-black/60': (!disabled) && (!onChange),
        },
      )}
      style={
        color
          ? { borderColor: color }
          : undefined
      }
    >
      {(isSelected)
        ? (
            <div
              className={classNames(
                'w-full h-full rounded-full',
                {
                  'bg-pxCheckActive': (!disabled) && onChange,
                  // 'bg-[#e5e7eb]': disabled,
                  'bg-black/80': disabled || (!onChange),
                },
              )}
              style={(color)
                ? { backgroundColor: color }
                : undefined}
            />
          )
        : null}
    </div>
  );

  if (disabled || (!onChange)) {
    if (!label) {
      return (
        <div
          id={id}
          className={classNames(
            'w-7 h-7 shrink-0 p-1.5',
            className,
          )}
        >
          {buttonContent}
        </div>
      );
    }

    return (
      <div
        id={id}
        className={className}
      >
        <Row start gap={0}>
          <div className="w-7 h-7 shrink-0 p-1.5">
            {buttonContent}
          </div>
          <span className="text-start">{label}</span>
        </Row>

      </div>
    );
  }

  if (!label) {
    return (
      <button
        type="button"
        onClick={handleClick}
        id={id}
        className={classNames(
          'w-7 h-7 shrink-0 p-1.5',
          className,
        )}
      >
        {buttonContent}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      id={id}
      className={className}
    >
      <Row start gap={0}>
        <div className="w-7 h-7 shrink-0 p-1.5">
          {buttonContent}
        </div>
        <span className="text-start">{label}</span>
      </Row>

    </button>
  );
}

RadioButton.propTypes = {
  className: PropTypes.string,
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  selected: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ]),
  selection: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
  ])),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  color: PropTypes.string,
};

export default RadioButton;
